import React, { useState, useEffect } from 'react';
import './styles.scss';
import { useConfigStore, useInterfaceStore } from '@/hooks/stores';
import { useCustomComponent } from '@/hooks';
import Components from './components';

const FloatingMessageComponent: React.FC = () => {
  const {
    ui: {
      showFloatingMessage,
      hoveringMessage,
      floatingMessageDelay,
      floatingMessageAutoHideDelay,
    },
  } = useConfigStore();
  const { widgetIsOpen, toggleWidgetOpen } = useInterfaceStore();

  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    // Don't show the message if the widget is already open
    if (widgetIsOpen) {
      setIsVisible(false);
      return;
    }

    // Show message after delay
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, floatingMessageDelay);

    // Auto-hide message after autoHideDelay
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
    }, floatingMessageDelay + floatingMessageAutoHideDelay);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [widgetIsOpen, floatingMessageDelay, floatingMessageAutoHideDelay]);

  // Hide message when widget opens
  useEffect(() => {
    if (widgetIsOpen) {
      setIsVisible(false);
    }
  }, [widgetIsOpen]);

  const handleClick = () => {
    toggleWidgetOpen();
    setIsVisible(false); // Hide immediately when clicked
  };

  if (!isVisible || widgetIsOpen || showFloatingMessage === false) {
    return null;
  }

  return (
    <Components.Container onClick={handleClick}>
      <Components.Content>
        <Components.Text>{hoveringMessage}</Components.Text>
        <Components.Arrow />
      </Components.Content>
    </Components.Container>
  );
};

export const FloatingMessage: React.FC = props => {
  const Component = useCustomComponent({
    componentName: 'FloatingMessage',
    Component: FloatingMessageComponent,
  });

  return <Component {...props} />;
};
